import { KeyboardArrowLeft, KeyboardArrowRight, ViewInAr } from '@mui/icons-material';
import { Box, IconButton, MobileStepper, styled, TextField, useTheme } from '@mui/material';

import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import React, { FC, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { isProdEnv } from '../../../../../environment.enum';
import { UnitWish } from '../../../../../models/unit-wish-list';
import { unitBaseTypeEnum } from '../../../../../unit-base-type-enum';
import styles from './LivingMixUnitCard.module.css';

export enum unitSubtypeEnum {
    apartment = 'apartment',
    maisonette = 'maisonette',
}


export interface UnitWishModel {
    type: unitBaseTypeEnum,
    subtype: unitSubtypeEnum,
    active: boolean;
    dirty: boolean;
    images: { imageSrc: string, label: string }[];
    notes: string[];
    squareMeters?: number[];
}

export interface UnitWishExtension extends UnitWishModel, UnitWish { }

interface LivingMixUnitCardProps {
    data: UnitWishExtension,
    buildingType: string,
    onClick: (type: unitBaseTypeEnum) => void,
    onChange: (type: unitBaseTypeEnum, percentage: number) => void,
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#fff',
        color: '#2819FF',
        maxWidth: 220,
        padding: 15,
        left: -20,
        borderRadius: 15,
        fontSize: theme.typography.pxToRem(12),
        border: '2px solid #2819FF',
    },
}));
const LivingMixUnitCard: FC<LivingMixUnitCardProps> = ({ data, buildingType, onClick, onChange }) => {

    const theme = useTheme();
    const [activeStep, setActiveStep] = useState(0);
    const maxSteps = data.images.length;

    const [percentage, setPercentage] = useState<string>('');

    useEffect(() => {
        if (data.percentage.toString() !== percentage) {
            setPercentage(data.percentage.toString());
        }
    }, [data.percentage])

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    return (
        <Box sx={{ maxWidth: 400, flex: 1, height: 'fit-content' }}>
            {
                !isProdEnv() && data.subtype === unitSubtypeEnum.apartment && buildingType !== 'Rijwoning' &&
                <HtmlTooltip className={styles.tooltip} placement='right' sx={{ marginLeft: '-20px' }} title={
                    <div>Ontdek in 3D en VR</div>
                }>
                    <Link target="_blank" className={styles.vrLink} to={`/vr?model=${buildingType.toLowerCase()}_${data.uiName.toLowerCase()}`}>
                        <IconButton
                            size="large"
                        >
                            <ViewInAr />
                        </IconButton>
                    </Link>
                </HtmlTooltip>
            }
            <button onClick={() => onClick(data.type)} className={
                (data.active ? styles.active + ' ' + styles.card : styles.disabled + ' ' + styles.card)}>
                <Box
                    component="img"
                    sx={{
                        height: 255,
                        display: 'block',
                        maxWidth: 400,
                        overflow: 'hidden',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                    }}
                    src={data.images[activeStep].imageSrc}
                    alt={data.images[activeStep].label} />
            </button>
            {
                data.images.length > 1 &&
                <MobileStepper
                    steps={maxSteps}
                    classes={{
                        root: styles.stepper,
                        dots: styles.dots
                    }}
                    position="static"
                    activeStep={activeStep}
                    variant="dots"
                    nextButton={<IconButton
                        className={styles.up}
                        color="secondary"
                        size="small"
                        onClick={handleNext}
                        disabled={activeStep === maxSteps - 1}
                    >
                        {theme.direction === 'rtl' ? (
                            <KeyboardArrowLeft />
                        ) : (
                            <KeyboardArrowRight />
                        )}
                    </IconButton>}
                    backButton={<IconButton
                        className={styles.up}
                        size="small"
                        onClick={handleBack}
                        disabled={activeStep === 0}>
                        {theme.direction === 'rtl' ? (
                            <KeyboardArrowRight />
                        ) : (
                            <KeyboardArrowLeft />
                        )}
                    </IconButton>} />
            }
            <div>
                <div className={styles.title}>
                    {data.uiName}
                </div>
                <div className={styles.description}>
                    {data.squareMeters.length > 1 ? `${data.squareMeters[0]} - ${data.squareMeters[1]} ` : `${data.squareMeters[0]} `}m<sup>2</sup>
                </div>
                <div className={styles.ps}>
                    {data.notes.map((note, i) => (<div key={i}>{note}</div>))}
                </div>
                <TextField
                    id={"mix-percentage" + data.type}
                    disabled={!data.active}
                    label="%"
                    type="number"
                    size="small"
                    className={styles.formControl}
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                    value={percentage}
                    onChange={(e) => {
                        const val = e.target.value;
                        setPercentage(val);
                    }}
                    onBlur={() => {
                        if (percentage && data.percentage.toString() !== percentage) {
                            if (!isNaN(+percentage)) {
                                onChange(data.type, +percentage)
                            } else {
                                onChange(data.type, 0)
                                setPercentage('0');
                            }
                        }
                    }}
                />
            </div>
        </Box>
    );
};

export default LivingMixUnitCard;